import { useCallback, useMemo } from "react";

import { Flex, Grid, Heading, Link, Text, TextField } from "@radix-ui/themes";
import {
  FieldErrors,
  useForm,
  Validate,
  ValidateResult,
} from "react-hook-form";

import {
  CompanyType,
  JobRole,
  ShippingDetails,
  UpdateUserRequest,
  User,
} from "../../api/public";
import {
  AddressInput,
  Popover,
  SelectWithController,
} from "../../common/components";
import { Button } from "../../common/components/Button";
import Textarea from "../../common/components/Textarea";
import { useValidateUserEmail } from "../../hooks/useValidateUserEmail";

const JOB_ROLE_OPTIONS = [
  {
    label: "Product Development / Formulation",
    value: JobRole.PRODUCT_DEVELOPMENT_FORMULATION,
  },
  { label: "Sales / Marketing", value: JobRole.SALES_MARKETING },
  { label: "Purchasing / Buyer", value: JobRole.PURCHASING },
  {
    label: "Production / Manufacturing",
    value: JobRole.PRODUCTION_MANUFACTURING,
  },
  { label: "Consultant", value: JobRole.CONSULTANT },
  { label: "Other", value: JobRole.OTHER },
];

const COMPANY_TYPE_OPTIONS = [
  { label: "Brand", value: CompanyType.BRAND },
  {
    label: "Contract Manufacturer / OEM / ODM",
    value: CompanyType.CONTRACT_MANUFACTURER,
  },
  { label: "Independent", value: CompanyType.INDEPENDENT },
  { label: "Ingredient Manufacturer", value: CompanyType.MANUFACTURER },
  { label: "Ingredient Distributor", value: CompanyType.DISTRIBUTOR },
  { label: "Press", value: CompanyType.PRESS },
  { label: "Other", value: CompanyType.OTHER },
];

type Props = {
  user?: User;
  quickVerification?: boolean;
  onSubmit?: (data: UpdateUserRequest) => void;
};

export const VerificationForm = ({
  user,
  quickVerification,
  onSubmit: onSubmitFromProps,
}: Props) => {
  const {
    watch,
    register,
    getValues,
    setValue,
    control,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
  } = useForm<UpdateUserRequest>({
    defaultValues: {
      ...user,
      tos_accepted: user?.tos_accepted_at ? true : false,
    },
    mode: "onSubmit",
  });

  const { mutateAsync } = useValidateUserEmail();

  const onValidSubmit = useCallback(() => {
    onSubmitFromProps?.(getValues());
  }, [getValues, onSubmitFromProps]);

  const bio = watch("bio");

  const validateEmail: Validate<string | undefined, UpdateUserRequest> =
    useCallback(
      async (email?: string) => {
        if (!email) {
          return "This field is required";
        }
        const response = await mutateAsync({ email });
        if (!response.data?.valid) {
          return "This email is not valid";
        }
        if (response.data.free) {
          if (bio) {
            return true;
          }
          setError("bio", {
            message:
              "Please fill out this field if you cannot provide a corporate email.",
          });
          return "Please use a corporate email. If you cannot provide one, please fill out the bio field.";
        }
        return true;
      },
      [bio, mutateAsync, setError]
    );

  const validateShippingDetails = useCallback(
    (shippingDetails?: ShippingDetails): ValidateResult => {
      let isValid = true;
      if (!shippingDetails?.address_line_1) {
        setError("shipping_details.address_line_1", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.city) {
        setError("shipping_details.city", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.country) {
        setError("shipping_details.country", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.zip) {
        setError("shipping_details.zip", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (!shippingDetails?.state) {
        setError("shipping_details.state", {
          message: "This field is required",
        });
        isValid = false;
      }
      if (isValid) {
        clearErrors("shipping_details");
      }
      return isValid;
    },
    [clearErrors, setError]
  );

  const shippingDetails = watch("shipping_details");

  const onSubmit = useCallback(() => {
    trigger(undefined, {
      shouldFocus: true,
    }).then((isValid) => {
      if (!isValid) return;

      if (quickVerification) {
        onValidSubmit();
        return;
      }

      if (validateShippingDetails(shippingDetails)) {
        onValidSubmit();
      }
    });
  }, [
    onValidSubmit,
    quickVerification,
    shippingDetails,
    trigger,
    validateShippingDetails,
  ]);

  const showBio = useMemo(() => {
    if (quickVerification) {
      return !!errors.bio;
    }
    return true;
  }, [errors.bio, quickVerification]);

  return (
    <Flex direction="column" gap="2">
      <Heading size="3">Profile Information</Heading>
      <Grid columns="1fr 1fr" gap="2">
        <Flex gap="1" direction="column">
          <TextField.Root
            type="text"
            placeholder="First Name"
            required={true}
            {...register("first_name", {
              required: "This field is required",
            })}
            color={errors.first_name && "red"}
          />
          {errors.first_name && (
            <Text color="red" size="1">
              {errors.first_name.message}
            </Text>
          )}
        </Flex>
        <Flex gap="1" direction="column">
          <TextField.Root
            type="text"
            placeholder="Last Name"
            required={true}
            {...register("last_name", {
              required: "This field is required",
            })}
            color={errors.last_name && "red"}
          />
          {errors.last_name && (
            <Text color="red" size="1">
              {errors.last_name.message}
            </Text>
          )}
        </Flex>
        <Flex gap="1" direction="column">
          <TextField.Root
            type="text"
            placeholder="Company Name"
            required={true}
            {...register("company_name", {
              required: "This field is required",
            })}
            color={errors.company_name && "red"}
          />
          {errors.company_name && (
            <Text color="red" size="1">
              {errors.company_name.message}
            </Text>
          )}
        </Flex>
        <SelectWithController
          control={control}
          name="company_type"
          placeholder={"Select company type..."}
          options={COMPANY_TYPE_OPTIONS}
          isMulti={false}
          required={true}
        />
      </Grid>
      <SelectWithController
        control={control}
        name="job_role"
        placeholder={"Select your role..."}
        options={JOB_ROLE_OPTIONS}
        isMulti={false}
        required={true}
      />
      <Heading size="3">Contact Information</Heading>
      <Flex gap="1" direction="column">
        <TextField.Root
          type="text"
          placeholder="Corporate Email"
          required={true}
          {...register("contact_email", {
            validate: validateEmail,
          })}
          color={errors.contact_email && "red"}
        />
        {errors.contact_email && (
          <Text size="1" color="red">
            {errors.contact_email.message}
          </Text>
        )}
      </Flex>
      <Flex gap="1" direction="column">
        {showBio && (
          <>
            <Flex gap="1">
              <Text size="1" color="gray" weight="medium">
                Bio — Tell us about yourself!
              </Text>
              <Popover
                title="Why we collect your Bio"
                content="Suppliers are more likely to respond to requests if they know who they are engaging with. Share a few details about yourself to make your requests stand out and build trust right from the start!"
                size="1"
                closeLabel="OK"
              >
                <Link size="1" style={{ cursor: "pointer" }}>
                  Why?
                </Link>
              </Popover>
            </Flex>
            <Textarea
              placeholder="I'm an independent cosmetic chemist and R&D expert with 10 years of experience. I graduated from University of Cincinnati with a chemistry degree and have previously worked at companies like Unilever, Goop, and Naturium. I recently launched my own clean skincare brand and also engage in consulting projects for select beauty industry clients."
              value={bio ?? ""}
              onChange={(e) =>
                setValue("bio", e.currentTarget.value, {
                  shouldDirty: true,
                })
              }
              color={errors.bio && "red"}
              rows={4}
            />
            {errors.bio && (
              <Text size="1" color="red">
                {errors.bio.message}
              </Text>
            )}
          </>
        )}
      </Flex>
      <TextField.Root
        type="text"
        placeholder="LinkedIn URL"
        {...register("linkedin_url")}
      />
      {!quickVerification && (
        <>
          <Heading size="3">Shipping Information</Heading>
          <AddressInput
            errors={errors.shipping_details as FieldErrors<ShippingDetails>}
            onChange={(shippingDetails) => {
              setValue(
                "shipping_details",
                {
                  address_line_1: shippingDetails?.address_line_1 ?? "",
                  address_line_2: shippingDetails?.address_line_2 ?? "",
                  city: shippingDetails?.city ?? "",
                  state: shippingDetails?.state ?? "",
                  zip: shippingDetails?.zip ?? "",
                  country: shippingDetails?.country ?? "",
                  fedex_account_number:
                    shippingDetails?.fedex_account_number ?? "",
                  ups_account_number: shippingDetails?.ups_account_number ?? "",
                  dhl_account_number: shippingDetails?.dhl_account_number ?? "",
                },
                {
                  shouldDirty: true,
                }
              );
            }}
            value={shippingDetails}
          />
          <Heading size="3">Carrier Information</Heading>
          <Grid columns="auto 1fr" gap="2" align="center">
            <Text size="1" color="gray" as="label" weight="medium">
              FedEx #
            </Text>
            <TextField.Root
              type="text"
              {...register("shipping_details.fedex_account_number")}
            />
            <Text size="1" color="gray" as="label" weight="medium">
              UPS #
            </Text>
            <TextField.Root
              type="text"
              {...register("shipping_details.ups_account_number")}
            />
            <Text size="1" color="gray" as="label" weight="medium">
              DHL #
            </Text>
            <TextField.Root
              type="text"
              {...register("shipping_details.dhl_account_number")}
            />
          </Grid>
        </>
      )}
      <Heading size="3">Terms & Conditions</Heading>
      <Flex gap="2" align="start">
        <input
          type="checkbox"
          defaultChecked={!!user?.tos_accepted_at}
          {...register("tos_accepted", {
            required: "This acknowledgement is required for verified accounts",
          })}
        />
        <Flex direction="column" gap="1">
          {/* TODO: update this copy */}
          <Text size="1">
            By checking this box, you acknowledge and permit Potion to share
            some parts of your profile with suppliers, in order to facilitate
            the sample request process.
          </Text>
          {errors.tos_accepted && (
            <Text size="1" color="red">
              {errors.tos_accepted.message}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex>
        <Button onClick={onSubmit} onSubmit={onSubmit} type="submit">
          Submit
        </Button>
      </Flex>
    </Flex>
  );
};
